import * as angular from 'angular';
import * as _ from 'lodash';

import { GroupChangeBase, ICentralActions } from '../groupChangeBase';
import { guidEmpty } from '../../../common';
import { ComponentDefinition, FlatGroup } from '../../../models';

interface IViewDiff {
    approvalStrategy: string;
    change: any;
    changeGuid: string;
    depth: number;
    groupName: string;
    guid: string;
    missGroup: boolean;
    missTemplate: boolean;
    parentGuid: string;
    propertyChanges: any[];
    templateApprovalStrategy: string;
    templateGuid: string;
    templateName: string;
}

class GroupChangesOverview extends GroupChangeBase implements angular.IComponentController {
    display: IViewDiff[];
    editorOptions: any = {};
    isLoading: boolean;

    private readonly _missingGroupName: string;
    private readonly _unknown: string;

    static $inject = ['$translate'];

    constructor($translate: angular.translate.ITranslateService,) {
        super();
        this._missingGroupName = $translate.instant('missing group');
        this._unknown = $translate.instant('Unknown');
    }

    $onInit() {
        this.editorOptions = {
            lineWrapping: true,
            lineNumbers: true
        };

        if (this.master) {
            const self = this;
            if (this.share && this.share.diff) {
                this.share.diff.applyAllFromTemplate = () => this.applyAllFromTemplate(self);
                this.share.diff.applyAllFromGroup = () => this.applyAllFromGroup(self);
                this.share.diff.ignoreAll = () => this.ignoreAll(self);
            }
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['groups']) {
            this.refresh();
        }

        if (changesObj['templates']) {
            this.refresh();
        }
    }

    lineClick(change) {
        if (change.property === 'Script') {

            if (!this.share.openScript) {
                this.share.openScript = [];
            }

            this.share.openScript[change.guid] = !this.share.openScript[change.guid];
        }
    }

    findChange(guid: string): any {
        return _.find(this.changes, c => c.guid === guid);
    }

    isSelected(guid): boolean {
        return this.share.selectedDiff && (this.share.selectedDiff.fromTemplate[guid] && this.master ||
            this.share.selectedDiff.fromGroup[guid] && !this.master);
    }

    isGroupTreated(g): boolean {
        if (g && g.change) {
            return this.isTreated(g.change.guid);
        }

        if (g.missGroup || g.missTemplate) {
            const parent = _.find(this.display, e => e.guid === g.parentGuid);
            return parent ? this.isGroupTreated(parent) : false;
        }

        return false;
    }

    isGroupSelected(g): boolean {
        if (g && g.change) {
            return this.isSelected(g.change.guid);
        }

        if (g.missGroup || g.missTemplate) {
            const parent = _.find(this.display, e => e.guid === g.parentGuid);
            return parent ? this.isGroupSelected(parent) : false;
        }

        return false;
    }

    private refresh() {
        if (this.changes && this.templates && this.groups) {
            const display = [];
            // Ignore children differences if template has no children
            const ignoreChildrens = this.templates.length === 1;
            const firstTemplate = this.templates[0].guid;
            this.groups.forEach(g => {
                this.refreshGroup(g, ignoreChildrens, firstTemplate, display);
            });
            this.display = display;
            this.createAlignButtons();
        }
    }

    private refreshGroup(g: FlatGroup, ignoreChildrens: boolean, firstTemplate: string, display: any[]) {
        let foundTemplate = true;
        let change: any = null;

        // find associate template
        const t = g.templateGuid !== guidEmpty
            ? _.find(this.templates, tmp => tmp.guid === g.templateGuid)
            : this.findInTemplateWithSameName(g);

        if (!t) {
            const diffObject = _.find(this.changes, c => c.objectGuid === g.guid);
            if (diffObject) {
                change = _.find(diffObject.differenceCollection,
                    c => c['property'] === null && c['copyValue'] === g.guid);
            }
            foundTemplate = false;
        }

        // find all the changes associated with this group concerning a property
        // i.e. : not a missing group but changed property
        let groupChanges: any[] = this.changes
            .filter(c => c.objectGuid === g.guid)
            .map(c => c.differenceCollection);

        groupChanges = _.flatten(groupChanges);
        groupChanges = groupChanges.filter(c => c.property !== null);

        const line = this.getLine(foundTemplate, t, g, ignoreChildrens, change, groupChanges, firstTemplate);
        display.push(line);

        // find the missing group in template, ie:
        // find all the changes regarding current template not concerning a property
        if (foundTemplate) {

            // changes regarding a template whom i am parent
            this.changes.forEach(diffObject => {
                const foundTpl = _.find(this.templates, tpl => tpl.guid === diffObject.objectGuid &&
                    tpl.parentGUID === t.guid);

                if (foundTpl) {
                    diffObject.differenceCollection.filter(c => c['property'] === null).forEach(c => {
                        const missings = this.createMissingGroup(t, c);
                        missings.forEach(m => display.push(m));
                    });
                }
            });
        }
    }

    private getLine(foundTemplate: boolean, t: FlatGroup, g: FlatGroup, ignoreChildrens: boolean, change: any, groupChanges: any[], firstTemplate: string) {
        const templateGuid = foundTemplate ? t.guid : guidEmpty;
        const missingGroupName = (ignoreChildrens ? ' ' : this._missingGroupName);
        return {
            guid: g.guid,
            parentGuid: g.parentGUID,
            groupName: g.name,
            templateName: foundTemplate
                ? t.name
                : missingGroupName,
            templateGuid: templateGuid,
            depth: (<any>g).depth,
            missTemplate: !foundTemplate && !ignoreChildrens,
            missGroup: false,
            changeGuid: foundTemplate || !change ? undefined : change.guid,
            change: change,
            propertyChanges: groupChanges,
            approvalStrategy: g.approvalStrategy ? g.approvalStrategy.name : '',
            templateApprovalStrategy: foundTemplate
                ? t.approvalStrategy?.name ?? ''
                : this._unknown,
            ignoreFolder: {
                // master
                true: ignoreChildrens && templateGuid !== firstTemplate,
                // slave
                false: false
            }
        };
    }

    private findInTemplateWithSameName(g: FlatGroup): FlatGroup {
        const parent = _.find(this.groups, group => group.guid === g.parentGUID);
        const templates = this.templates.filter(tmp => tmp.name === g.name && parent.templateGuid === tmp.parentGUID);

        return (templates.length === 1)
            ? templates[0]
            : null;
    }

    private createAlignButtons() {
        const self = this;
        this.share.central = [];

        this.display.forEach(g => {

            if ((g.missGroup || g.missTemplate) && g.changeGuid) {
                this.share.central.push(this.getCentralAction(g, self));
            } else {
                this.share.central.push({});
            }

            if (g.propertyChanges) {
                g.propertyChanges.forEach(change => {
                    const actions = this.getChangeCentralAction(self, change);

                    this.share.central.push(actions);
                });
            }

        });
    }

    private getCentralAction(g: IViewDiff, self: this): ICentralActions {
        return {
            leftAction: g.missTemplate && g.changeGuid
                ? () => self.share.diff.applyFromGroup(g.change)
                : undefined,
            leftSelected: () => self.share.selectedDiff.fromGroup[g.change.guid],
            ignoreAction: () => self.share.diff.ignore(g.change),
            ignoreSelected: () => self.share.selectedDiff.ignores[g.change.guid],
            rightAction: g.missGroup && g.changeGuid
                ? () => self.share.diff.applyFromTemplate(g.change)
                : undefined,
            rightSelected: () => self.share.selectedDiff.fromTemplate[g.change.guid],
            isTreated: () => this.isTreated(g.change.guid),
            changeGuid: g.change.guid
        };
    }

    private getChangeCentralAction(self: this, change: any): ICentralActions {
        const actions: ICentralActions = {
            leftAction: () => self.share.diff.applyFromGroup(change),
            leftSelected: () => self.share.selectedDiff.fromGroup[change.guid],
            ignoreAction: () => self.share.diff.ignore(change),
            ignoreSelected: () => self.share.selectedDiff.ignores[change.guid],
            rightAction: () => self.share.diff.applyFromTemplate(change),
            rightSelected: () => self.share.selectedDiff.fromTemplate[change.guid],
            isTreated: () => this.isTreated(change.guid),
            changeGuid: change.guid
        };

        if (change.property === 'TemplateGuid') {
            delete actions['leftAction'];
        }
        return actions;
    }

    private createMissingGroup(group, change): IViewDiff[] {
        const template = _.find(this.templates, tmp => tmp.guid === change['originalValue']);
        return this.createRecurseMissingGroup(group.depth + 1, change, template);
    }

    private createRecurseMissingGroup(depth: number, change: any, template: any): IViewDiff[] {
        let ret: IViewDiff[] = [];

        ret.push({
            guid: template.guid,
            parentGuid: template.parentGUID,
            groupName: this._missingGroupName,
            templateName: template.name,
            templateGuid: template.guid,
            depth: depth,
            missTemplate: false,
            missGroup: true,
            changeGuid: change.guid,
            change: change,
            propertyChanges: [],
            approvalStrategy: undefined,
            templateApprovalStrategy: undefined
        });

        depth++;
        const children = this.templates.filter(tmp => tmp.parentGUID === template.guid);
        if (children) {
            children.forEach(child => {
                ret = ret.concat(this.createRecurseMissingGroup(depth, '', child));
            });
        }

        return ret;
    }

    private applyAllFromTemplate(self) {
        self.display.forEach(g => {
            // group changes
            if (g.changeGuid && !this.isTreated(g.changeGuid)) {
                if (g.missGroup) {
                    self.share.diff.applyFromTemplate(g.change);
                } else if (g.missTemplate) {
                    self.share.diff.ignore(g.change);
                }
            }
            // property changes
            g.propertyChanges.forEach(c => {
                if (!this.isTreated(c.guid)) {
                    self.share.diff.applyFromTemplate(c);
                }
            });
        });
    }

    private applyAllFromGroup(self) {
        self.display.forEach(g => {
            // group changes
            if (g.changeGuid && !this.isTreated(g.changeGuid)) {
                if (g.missTemplate) {
                    self.share.diff.applyFromGroup(g.change);
                } else if (g.missGroup) {
                    self.share.diff.ignore(g.change);
                }
            }
            // property changes
            g.propertyChanges.forEach(c => {
                if (!this.isTreated(c.guid)) {
                    self.share.diff.applyFromGroup(c);
                }
            });
        });
    }

    private ignoreAll(self) {
        self.display.forEach(g => {
            // group changes
            if (g.changeGuid && !this.isTreated(g.changeGuid)) {
                self.share.diff.ignore(g.change);
            }
            // property changes
            g.propertyChanges.forEach(c => {
                if (!this.isTreated(c.guid)) {
                    self.share.diff.ignore(c);
                }
            });
        });
    }
}

export const wtGroupChangesOverviewWidget: ComponentDefinition = {
    componentId: 'wtGroupChangesOverview',
    controller: GroupChangesOverview,
    controllerAs: 'vm',
    template: require('./groupChangesOverview.component.html'),
    bindings: {
        changes: '=',
        groups: '<',
        templates: '<',
        master: '=?',
        share: '='
    }
};
